import { useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { DONOR_ECONOMIC_ROWS, PENDING_ECONOMIC_KPIS, RATE_ROWS } from '../../../core/constants/kpis';
import { envelopeData, envelopeModules } from '../../../core/network/envelope';
import { periodLabel } from '../../../core/utils/date';
import { fmtMoney, fmtNum, fmtPct } from '../../../core/utils/format';
import { useClientDonors } from '../contexts/donors';
import { useClientDonorsKpi } from '../contexts/donors-kpi';
import { useClientLiquidations } from '../contexts/liquidations';
import InvestorHero from './investor-hero';

import '../styles/overview.css';

// TSS vite/03 — "Mis vacas": grilla read-only de las donadoras co-poseídas
// (cowhousing) + bloque agregado del lote. Contract: public-client-channel.md.
//
// Los hooks devuelven el SOBRE público completo (no la forma plana de
// parcerías): items/pagination viven en envelope.data, y available_modules
// en la raíz del sobre. El backend es dueño de los números (gross_value,
// stake_value, offspring_return ya derivados — 0047); acá solo se formatea
// con fmtMoney y se mapea key → label.
// Paginación offset (contract: /donors limit def 20 / máx 100).

const PAGE_SIZE = 20;

// Económico aún sin canal público (E6–E8): solo señalización "Próximamente" —
// la fuente es PENDING_ECONOMIC_KPIS; sin fetchers ni datos cableados.
// (`liquidation`/`share` salieron en D-009: ya son datos reales.)
const PENDING_LABELS = {
    account_statement: 'Estado de cuenta',
    net_roi:           'ROI neto',
};

// Agregado "cómo rinde mi lote" (public/stats?view=kpi → data.donors_kpi).
// Se muestra solo si el módulo está en available_modules del sobre — su
// ausencia significa "N/A para tu rol", y loading/error quedan silenciosos
// (el bloque es opcional, la grilla no depende de él).
function DonorsKpiBlock() {
    const { data: envelope } = useClientDonorsKpi();

    const kpi = envelope?.data?.donors_kpi;
    const modules = envelope?.available_modules ?? [];
    if (!kpi || !modules.includes('donors_kpi')) return null;

    // donors_kpi.rates es un subconjunto {viability, fertilization, pregnancy,
    // survival} (kpi-catalog G3) — se filtra RATE_ROWS para reusar sus labels.
    const rates = kpi.rates || {};
    const rows = RATE_ROWS.filter((row) => rates[row.key]);

    return (
        <section className="vacas__kpi">
            <div className="vacas__kpi-head">
                <h3 className="vacas__kpi-title">Tu lote</h3>
                <span className="vacas__kpi-meta">
                    {fmtNum(kpi.count)} donadora{kpi.count === 1 ? '' : 's'}
                    {kpi.avg_percent != null && ` · participación media ${fmtPct(kpi.avg_percent)}`}
                </span>
            </div>

            {rows.length > 0 && (
                <div className="vacas__kpi-rates">
                    {rows.map((row) => {
                        const rate = rates[row.key];
                        return (
                            <span
                                key={row.key}
                                className={`vacas__kpi-rate vacas__kpi-rate--${rate.qualifier || 'none'}`}
                            >
                                {row.label} <strong>{fmtPct(rate.value)}</strong>
                            </span>
                        );
                    })}
                </div>
            )}
        </section>
    );
}

function DonorCard({ donor, payout }) {
    // offspring_return vive plano en el item del listado (E1–E3, view=summary).
    const money = {
        gross_value:      donor.gross_value,
        stake_value:      donor.stake_value,
        offspring_return: donor.offspring_return,
    };

    // «Tu reparto del mes» (D-009): último reparto CONGELADO de la liquidación
    // (share_amount) — concepto distinto de «Tu participación» (percent, arriba)
    // y del valor de la participación (stake_value). Solo formateo.
    const last = payout?.last_liquidation;

    return (
        <Link to={`/mis-vacas/${donor.id}`} className="vacas-card">
            <div className="vacas-card__head">
                <span className="vacas-card__name">{donor.name || `Donadora #${donor.id}`}</span>
                <span className="vacas-card__percent">{fmtPct(donor.percent)}</span>
            </div>

            <div className="vacas-card__meta">
                <span className="vacas-card__number">Nº {donor.number}</span>
                {donor.register && <span>{donor.register}</span>}
            </div>

            <dl className="vacas-card__economics">
                {DONOR_ECONOMIC_ROWS.map((row) => (
                    <div key={row.key} className="vacas-card__economic">
                        <dt className="vacas-card__economic-label">{row.label}</dt>
                        <dd className="vacas-card__economic-value">
                            {row.money ? fmtMoney(money[row.key]) : fmtNum(money[row.key])}
                        </dd>
                    </div>
                ))}
            </dl>

            {last && (
                <div className="vacas-card__payout">
                    <span className="vacas-card__payout-label">
                        Tu reparto del mes
                        <span className="vacas-card__payout-period">
                            {' '}({periodLabel(last.period_year, last.period_month)})
                        </span>
                    </span>
                    <span className="vacas-card__payout-value">
                        {fmtMoney(last.my_share?.share_amount)}
                    </span>
                </div>
            )}
        </Link>
    );
}

export default function VacasOverview() {
    const [params, setParams] = useSearchParams();

    const offset = Math.max(0, Number(params.get('offset')) || 0);

    const { data: envelope, isLoading, isFetching, isError, error } =
        useClientDonors({ view: 'summary', limit: PAGE_SIZE, offset });

    // Reparto mensual (D-009): índice animal.id → item de liquidations. Se
    // muestra solo si el módulo `economic` viene en el sobre (gated
    // donor_owner); sin módulo o sin entrada para la vaca, la fila no aparece.
    const { data: liqEnvelope } = useClientLiquidations();
    const payoutsByAnimal = useMemo(() => {
        if (!envelopeModules(liqEnvelope).includes('economic')) return new Map();
        const rows = envelopeData(liqEnvelope).items ?? [];
        return new Map(rows.map((row) => [row.animal?.id, row]));
    }, [liqEnvelope]);

    // Sobre público completo — NO copiar la forma plana de parcerías.
    const items = envelope?.data?.items ?? [];
    const pagination = envelope?.data?.pagination ?? {};
    const total = pagination.total ?? null;

    const hasPrev = offset > 0;
    const hasNext = total != null ? offset + items.length < total : items.length === PAGE_SIZE;

    const goTo = (nextOffset) => {
        const next = new URLSearchParams(params);
        if (nextOffset > 0) next.set('offset', String(nextOffset));
        else next.delete('offset');
        setParams(next, { replace: true });
    };

    if (isLoading) {
        return <div className="vacas__loading">Cargando donadoras...</div>;
    }

    if (isError) {
        return (
            <div className="vacas__error">
                {error?.message || 'No se pudieron cargar tus donadoras'}
            </div>
        );
    }

    return (
        <section className="vacas">
            {/* Primera impresión del inversionista: gráfico "tipo trading" de su
                inversión + KPI de su mejor rendimiento. */}
            <InvestorHero />

            <header className="vacas__header">
                <h2 className="vacas__title">Tu portafolio</h2>
                <span className="vacas__count">
                    {total != null ? total : items.length} donadora{(total ?? items.length) === 1 ? '' : 's'}
                </span>
            </header>

            <DonorsKpiBlock />

            {!items.length ? (
                <p className="vacas__empty">Aún no tienes donadoras en cowhousing.</p>
            ) : (
                <div className={`vacas__list ${isFetching ? 'vacas__list--refreshing' : ''}`}>
                    {items.map((donor) => (
                        <DonorCard key={donor.id} donor={donor} payout={payoutsByAnimal.get(donor.id)} />
                    ))}
                </div>
            )}

            {(hasPrev || hasNext) && (
                <nav className="vacas__pager">
                    <button
                        className="vacas__pager-btn"
                        onClick={() => goTo(offset - PAGE_SIZE)}
                        disabled={!hasPrev || isFetching}
                    >
                        ← Anteriores
                    </button>
                    <span className="vacas__pager-info">
                        {offset + 1}–{offset + items.length}
                    </span>
                    <button
                        className="vacas__pager-btn"
                        onClick={() => goTo(offset + PAGE_SIZE)}
                        disabled={!hasNext || isFetching}
                    >
                        Siguientes →
                    </button>
                </nav>
            )}

            <section className="vacas__pending">
                <h3 className="vacas__pending-title">Próximamente</h3>
                <div className="vacas__pending-chips">
                    {PENDING_ECONOMIC_KPIS.map((key) => (
                        <span key={key} className="vacas__chip">{PENDING_LABELS[key] || key}</span>
                    ))}
                </div>
            </section>
        </section>
    );
}
